import { useApolloClient, useMutation } from "@apollo/client";
import { useEffect, useRef, useState } from "react";
import { AttendeeRole, OnlineConference } from "types/graphql-schema";

import { pendingChatPromptVar } from "@apps/ask-ai/ask-ai-context";
import createOrUpdateMeetingInExternalCalendarMutation from "@apps/meeting-dialog/graphql/create-or-update-meeting-in-external-calendar-mutation";
import MemberEditDialog from "@apps/org-settings/components/member-edit-dialog";
import useLabel from "@apps/use-label/use-label";
import useUiPreferenceCache, {
  UiPreferenceCache,
} from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import {
  currentOrganizationVar,
  currentUserVar,
  errorNotificationVar,
} from "@cache/cache";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import { meetingEditType } from "@helpers/constants";
import { classNames } from "@helpers/css";

import ProfileTabEmptyState from "../components/profile-tab-empty-state";
import getProfileQuery from "../graphql/get-profile-query";
import GetStartedCard from "./get-started-card";
import getProfileOneononesQuery from "./graphql/get-profile-oneonones-query";
import ImportOneononeDialog from "./import-oneonone-dialog";
import { meetingsShowingEmptyCardsVar } from "./oneonone-empty-cards-state";

// This tab always operates on a formal 1-on-1, so prep with the dedicated
// 1-on-1 skill rather than the general (team/group) meeting-prep skill.
const oneononePrepSkill = "/1-on-1-prep";

const displayName = (firstName: string | null | undefined, email: string) =>
  firstName?.trim() || email;

// The other person in a 1-on-1 (the non-subject participant, who also
// facilitates). On a report's profile this is the current user; on the current
// user's own profile it's their primary manager.
export type OneononeCounterpart = {
  id: number;
  firstName: string | null | undefined;
  email: string;
};

type Props = {
  // Subject of the 1-on-1. Used for the per-profile dismissal pref, the
  // standalone-mode draft attendees, and import-dialog scoping.
  profileId: number;
} & (
  | {
      // In-meeting mode: cards render inside a meeting view. Clicking an option
      // just dismisses for this profile; the meeting itself already exists.
      meetingId: number;
      profileFirstName?: never;
      profileEmail?: never;
      profileManager?: never;
      canAutoCreateDraft?: never;
    }
  | {
      // Standalone mode (no meeting scheduled yet): Create empty meeting /
      // Prepare with Topicflow AI create a draft via the mutation and redirect
      // to it. profileFirstName/email feed the draft title + attendee list.
      meetingId?: undefined;
      profileFirstName: string | null | undefined;
      profileEmail: string;
      // The profile user's primary manager. Only used (and only present) on the
      // current user's own profile, where the manager is the 1-on-1 counterpart.
      profileManager?: OneononeCounterpart | null;
      // Whether merely opening this profile may create a draft 1-on-1 on its
      // own. True only for the people the current user actually runs 1-on-1s
      // with (their own profile, and their direct reports) — see
      // `shouldAutoCreateDraft` below.
      canAutoCreateDraft: boolean;
    }
);

const OneononeMeetingEmptyCards = ({
  meetingId,
  profileId,
  profileFirstName,
  profileEmail,
  profileManager,
  canAutoCreateDraft,
}: Props) => {
  const label = useLabel();
  const link = useLink();
  const currentUser = currentUserVar();

  // A 1-on-1 always has two people: the subject (the profile) and a counterpart
  // who facilitates. On a report's profile the counterpart is the current user;
  // on the current user's OWN profile it's their manager. Facilitating the
  // profile user themselves isn't a valid 1-on-1, so we never make the current
  // user the counterpart of their own profile.
  const isOwnProfile = profileId === currentUser.id;
  const counterpart: OneononeCounterpart | null = isOwnProfile
    ? (profileManager ?? null)
    : {
        id: currentUser.id,
        firstName: currentUser.firstName,
        email: currentUser.email,
      };
  const { uiPreferenceCache, saveUiPreference } = useUiPreferenceCache();
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showSetManagerDialog, setShowSetManagerDialog] = useState(false);
  // Standalone mode auto-creates a draft; if that fails we fall back to showing
  // the get-started cards so the user can retry / pick another option.
  const [autoCreateError, setAutoCreateError] = useState(false);
  const organization = currentOrganizationVar();
  const apolloClient = useApolloClient();

  // Re-fetch the profile so a manager set via the dialog flows back into
  // `profileManager`, which swaps the no-manager placeholder for the create
  // cards without a page reload.
  const handleCloseSetManagerDialog = () => {
    setShowSetManagerDialog(false);
    apolloClient.refetchQueries({ include: [getProfileQuery] });
  };

  // Track the meeting id (when present) so MeetingDropdownMenu can hide
  // "Discard Draft Meeting" while these cards are showing.
  useEffect(() => {
    if (meetingId === undefined) return;
    const next = new Set(meetingsShowingEmptyCardsVar());
    next.add(meetingId);
    meetingsShowingEmptyCardsVar(next);
    return () => {
      const cleanup = new Set(meetingsShowingEmptyCardsVar());
      cleanup.delete(meetingId);
      meetingsShowingEmptyCardsVar(cleanup);
    };
  }, [meetingId]);

  const meetingTitle =
    profileEmail !== undefined && counterpart
      ? `${label("1-on-1")}: ${displayName(
          counterpart.firstName,
          counterpart.email,
        )} - ${displayName(profileFirstName, profileEmail)}`
      : "";

  const [createDraftMeeting, { loading: creatingDraft }] = useMutation(
    createOrUpdateMeetingInExternalCalendarMutation,
    {
      refetchQueries: [getProfileOneononesQuery],
      onError: () => {
        setAutoCreateError(true);
        errorNotificationVar({
          title: `Could not start your ${label("1-on-1")}`,
          timeout: 5000,
        });
      },
    },
  );

  // The draft is created asynchronously. If the user has navigated away (e.g. to
  // another profile tab) by the time it lands, skip the redirect below so the
  // auto-create doesn't yank them back to the 1-on-1 tab.
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // useUiPreferenceCache reads a snapshot at render time, so chained
  // saveUiPreference calls all merge against the SAME stale snapshot — later
  // calls clobber earlier ones. We always batch related updates into one call.
  const dismissedProfileIdsAfterDismiss = () => {
    const dismissed = uiPreferenceCache.oneononeEmptyCardsDismissedProfileIds;
    return dismissed.includes(profileId)
      ? dismissed
      : [...dismissed, profileId];
  };

  // Standalone-mode helper: persist any requested prefs, fire the mutation, then
  // redirect once the meeting is back. `afterCreate` runs with the new meeting
  // id so Prepare-with-AI can prime pendingChatPromptVar.
  //
  // We intentionally do NOT dismiss the empty-state pref here: the new "Want
  // help preparing?" card should surface on the freshly created draft, and the
  // user dismisses it from that card.
  const createDraftAndNavigate = (
    extraPrefs: Partial<UiPreferenceCache>,
    afterCreate?: (newMeetingId: number) => void,
  ) => {
    if (profileEmail === undefined || !counterpart) return;
    if (Object.keys(extraPrefs).length > 0) {
      saveUiPreference(extraPrefs);
    }
    createDraftMeeting({
      variables: {
        attendees: [
          { email: counterpart.email, role: AttendeeRole.Required },
          { email: profileEmail, role: AttendeeRole.Required },
        ],
        onlineConference: OnlineConference.Native,
        editType: meetingEditType.singleEvent,
        publish: false,
        title: meetingTitle,
        isFormalOneonone: true,
        // The counterpart facilitates so the profile user stays the subject —
        // a meeting a person facilitates for themselves isn't a valid 1-on-1.
        facilitatorId: counterpart.id,
        addTranscriptionBotToMeetingsByDefault: true,
      },
      onCompleted: ({ createOrUpdateMeetingInExternalCalendar }) => {
        const meeting = createOrUpdateMeetingInExternalCalendar?.meeting;
        if (!meeting?.meetingGroup || !isMountedRef.current) return;
        afterCreate?.(meeting.id);
        link.redirect(`/profile/${profileId}/oneonone/${meeting.id}`);
      },
    });
  };

  // On a profile with no 1-on-1 yet (standalone mode) and a valid counterpart,
  // skip the "how would you like to start" cards and immediately create a draft
  // meeting, redirecting to it. The cards remain for the in-meeting empty state
  // and as a fallback if the auto-create fails.
  //
  // `canAutoCreateDraft` keeps this to the people the current user actually runs
  // 1-on-1s with: their own profile and their direct reports. Everyone else — a
  // peer, a skip-level report, or an external person whose profile is only
  // visible because of a shared meeting — gets the cards, so simply looking at
  // their profile never creates a meeting behind the user's back (TF-1593).
  const shouldAutoCreateDraft =
    meetingId === undefined &&
    !!counterpart &&
    !autoCreateError &&
    !!canAutoCreateDraft;
  const hasAutoCreatedRef = useRef(false);
  useEffect(() => {
    if (shouldAutoCreateDraft && !hasAutoCreatedRef.current) {
      hasAutoCreatedRef.current = true;
      createDraftAndNavigate({});
    }
    // createDraftAndNavigate is a render-time closure; fire once when eligible.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAutoCreateDraft]);

  const handleCreateEmpty = () => {
    if (meetingId !== undefined) {
      saveUiPreference({
        oneononeEmptyCardsDismissedProfileIds:
          dismissedProfileIdsAfterDismiss(),
      });
      return;
    }
    createDraftAndNavigate({});
  };

  const handlePrepareWithAI = () => {
    // Open the meeting sidebar (which hosts AskAI) for both the standalone
    // meeting page and the profile 1-on-1 tab. Formal 1-on-1s render in the
    // profile tab, but the standalone key is kept in sync so the prompt fires
    // wherever the user happens to be.
    const sidebarPrefs: Partial<UiPreferenceCache> = {
      profileSidebarExpanded: true,
      meetingSidebarExpanded: true,
    };
    if (meetingId !== undefined) {
      saveUiPreference({
        ...sidebarPrefs,
        oneononeEmptyCardsDismissedProfileIds:
          dismissedProfileIdsAfterDismiss(),
      });
      pendingChatPromptVar({ meetingId, prompt: `${oneononePrepSkill} ` });
      return;
    }
    createDraftAndNavigate(sidebarPrefs, (newMeetingId) => {
      pendingChatPromptVar({
        meetingId: newMeetingId,
        prompt: `${oneononePrepSkill} `,
      });
    });
  };

  // Import notes dismisses only after a successful import so the cards stay
  // visible if the user opens the dialog and cancels out — they can still
  // pick a different option. ImportOneononeDialog redirects to the most recent
  // imported meeting itself, so we only persist the dismissal here.
  const handleImported = () => {
    saveUiPreference({
      oneononeEmptyCardsDismissedProfileIds: dismissedProfileIdsAfterDismiss(),
    });
  };

  // While the draft is being created (auto or via a card), and on the first
  // render before the auto-create effect fires, show a loading state instead of
  // the get-started cards so they never flash in standalone mode.
  if (creatingDraft || shouldAutoCreateDraft) {
    return <Loading>Starting your {label("1-on-1")}...</Loading>;
  }

  // Standalone mode with no counterpart to create against — this only happens on
  // the current user's own profile when they have no manager set up. A 1-on-1
  // needs a second person, so show an informational placeholder rather than
  // create actions that can't succeed.
  if (meetingId === undefined && !counterpart) {
    return (
      <>
        <div className="flex min-h-[70vh] flex-col items-center justify-center px-6">
          <div className="w-full max-w-lg">
            <ProfileTabEmptyState
              title="No manager set up"
              body={`You need a manager to start a ${label("1-on-1")}.`}
              buttonText="Set My Manager"
              onButtonClick={() => setShowSetManagerDialog(true)}
            />
          </div>
        </div>
        {showSetManagerDialog && (
          <MemberEditDialog
            organization={organization}
            memberId={currentUser.id}
            onClose={handleCloseSetManagerDialog}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div
        className={classNames(
          "flex flex-col items-center gap-12 px-6",
          // Standalone view (no meeting yet) centers vertically in the
          // viewport; in-meeting view just flows after the meeting header.
          meetingId === undefined
            ? "min-h-[70vh] justify-center py-12"
            : "pt-16 pb-12",
        )}
      >
        <div className="tf-header-2xl text-center">
          How would you like to start this {label("1-on-1")}?
        </div>
        <div className="w-full max-w-5xl @container">
          <div className="flex flex-col @4xl:flex-row gap-3 @4xl:gap-6 max-w-[500px] @4xl:max-w-none mx-auto">
            <GetStartedCard
              icon="file04"
              title="Create empty meeting"
              body="Start from a blank slate and capture notes right away."
              onClick={handleCreateEmpty}
            />
            <GetStartedCard
              icon="upload"
              title="Import notes"
              body={`Paste notes from past ${label("1-on-1", { pluralize: true })} to bring them into Topicflow.`}
              onClick={() => setShowImportDialog(true)}
            />
            <GetStartedCard
              icon="ai"
              title="Prepare with Topicflow AI"
              body="Let Topicflow AI suggest topics and talking points for this meeting."
              onClick={handlePrepareWithAI}
            />
          </div>
        </div>
      </div>

      {showImportDialog && (
        <ImportOneononeDialog
          profileId={profileId}
          onClose={() => setShowImportDialog(false)}
          onImported={handleImported}
        />
      )}
    </>
  );
};

export default OneononeMeetingEmptyCards;
